import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, Panel } from '../../../ui-kit';
import { fetchConfiguration } from '../../../services/roccoService';
import VoiceAgents from '../VoiceAgents';
import { useLive } from './live';

/**
 * How ROCCO is set up, exactly as it runs.
 *
 * Every line is read from the running system: the rules carry the calling
 * job's own numbers, the voice agents are checked against the provider, and
 * the opening lines are the ones the calls use. Nothing here is a control —
 * a setting is changed where it lives, and this page follows.
 */
export default function ConfigurationPage() {
  const { data, error, loading } = useLive(fetchConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      <Panel
        title="How a lead moves"
        subtitle="From the moment it arrives to the moment an advisor has it"
        loading={loading}
        loadingHeight={300}
      >
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>
                {rule.then}
              </Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <VoiceAgents
        agents={(data?.agents ?? []).map((agent) => ({
          ...agent,
          lines: [
            { label: 'First call', line: agent.opening },
            { label: 'A follow-up call', line: agent.follow_up_opening },
          ],
        }))}
        facts={data?.agent_facts ?? []}
        providerError={data?.provider_error}
        loading={loading}
      />

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel
          title="What each call finds out"
          subtitle="Read from the conversation when the call ends, so nobody types it in"
          loading={loading}
          loadingHeight={220}
        >
          <Stack spacing={1.25}>
            {(data?.finds_out ?? []).map((item) => (
              <Line key={item.key} title={item.label} detail={item.detail} />
            ))}
          </Stack>
        </Panel>

        <Panel
          title="Guardrails"
          subtitle="Held on every call, whatever the lead says"
          loading={loading}
          loadingHeight={220}
        >
          <Stack spacing={1.25}>
            {(data?.guardrails ?? []).map((rail) => (
              <Line key={rail.title} title={rail.title} detail={rail.detail} />
            ))}
          </Stack>
        </Panel>
      </Box>
    </Stack>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
